import React, { useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { useCookies } from "react-cookie";
import moment from "moment";
import { Progress, Spinner } from "reactstrap";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  PaginationComponent,
  Row,
} from "components/Component";
import Content from "layout/content/Content";
import Head from "layout/head/Head";
import { serviceGetWaCostRates, serviceGetWaCostReport } from "services/waCosts";
import { formatCurrencyIDR } from "utils/formatTransaction";
import { PROVIDER_LABEL, rateOn, sortRows } from "./waCostFormat";
import WaCostRates from "./WaCostRates";
import "./waCostMonitoring.css";

const ALLOWED_ROLES = ["ADMIN", "SUPER ADMIN", "SYSTEM ADMIN"];
const DATE = "YYYY-MM-DD";
const MONTH = "YYYY-MM";

const TAKE = 10;

// column = header of the first report column for that grouping.
const GROUP_OPTIONS = [
  { value: "DAY", label: "Harian", column: "Tanggal" },
  { value: "MONTH", label: "Bulanan", column: "Bulan" },
  { value: "CHANNEL", label: "Per Nomor", column: "Nomor" },
  { value: "DEPARTMENT", label: "Per Departemen", column: "Departemen" },
  { value: "CAMPAIGN", label: "Per Campaign", column: "Campaign" },
  { value: "PROVIDER", label: "Per Provider", column: "Provider" },
];

// Keys of DAY/MONTH rows are ISO dates, so they also sort chronologically.
const displayLabel = (groupBy, item) => {
  if (groupBy === "DAY") return moment(item.key, DATE).format("DD MMM YYYY");
  if (groupBy === "MONTH") return moment(item.key, "YYYY-MM").format("MMMM YYYY");
  if (groupBy === "PROVIDER") return PROVIDER_LABEL[item.key] || item.key;
  return item.label;
};

const TABS = [
  { key: "SUMMARY", label: "Ringkasan" },
  { key: "REPORT", label: "Laporan" },
  { key: "RATES", label: "Tarif" },
];

const COLUMNS = [
  { key: "label", label: null, align: "" }, // header comes from GROUP_OPTIONS
  { key: "messageCount", label: "Pesan Terkirim", align: "text-right" },
  { key: "billableCount", label: "Pesan Berbayar", align: "text-right" },
  { key: "totalCost", label: "Total Biaya", align: "text-right" },
];

const defaultFilter = () => ({
  startDate: moment().startOf("month").format(DATE),
  endDate: moment().format(DATE),
  groupBy: "DAY",
  provider: "",
});

// Mirrors the summary card layout: title + badge, amount, counts, quota bar.
const SummaryCardSkeleton = () => (
  <Col md="6" xl="4">
    <div className="card card-bordered h-100">
      <div className="card-inner">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <span className="wa-skeleton" style={{ width: "60%", height: 16 }} />
          <span className="wa-skeleton" style={{ width: 52, height: 18, borderRadius: 10 }} />
        </div>
        <span className="wa-skeleton mb-2" style={{ width: "40%", height: 26 }} />
        <span className="wa-skeleton mb-3" style={{ width: "70%", height: 12 }} />
        <span className="wa-skeleton mb-2" style={{ width: "100%", height: 8 }} />
        <span className="wa-skeleton" style={{ width: "35%", height: 10 }} />
      </div>
    </div>
  </Col>
);

const WaCostMonitoring = () => {
  const [cookies] = useCookies(["user"]);
  const isAllowed = ALLOWED_ROLES.includes(cookies.role?.name);
  const [tab, setTab] = useState("SUMMARY");
  const [filter, setFilter] = useState(defaultFilter);
  // groupBy travels with its rows so labels/headers never mix two requests.
  const [report, setReport] = useState({ rows: [], groupBy: "DAY" });
  const [summary, setSummary] = useState([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [sort, setSort] = useState({ key: "label", dir: "asc" });
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const latestSummaryRequest = useRef(0);

  const today = moment().format(DATE);
  const currentMonth = moment().format(MONTH);
  const [summaryMonth, setSummaryMonth] = useState(currentMonth);
  // A past month is summarised in full; the current one up to today.
  const summaryStart = moment(summaryMonth, MONTH).startOf("month").format(DATE);
  const summaryEnd = summaryMonth === currentMonth ? today : moment(summaryMonth, MONTH).endOf("month").format(DATE);

  const refreshReport = async () => {
    // Filters change faster than the report returns: only the newest
    // request may write state, or an older response overwrites it.
    const requestId = ++latestRequest.current;
    const requested = filter;
    setIsLoading(true);
    const resp = await serviceGetWaCostReport(cookies.SIDikey, requested);
    if (requestId !== latestRequest.current) return;
    setIsLoading(false);
    if (resp.status === 200) {
      setReport({ rows: resp.data, groupBy: requested.groupBy });
      setError("");
    } else {
      setReport({ rows: [], groupBy: requested.groupBy });
      const msg = resp.data?.message;
      // Validation errors arrive as an array of objects; never render those raw.
      setError(typeof msg === "string" ? msg : "Gagal memuat data biaya");
    }
  };

  // Free quota resets on the 1st, so the summary is one calendar month per number.
  const refreshSummary = async () => {
    const requestId = ++latestSummaryRequest.current;
    setIsSummaryLoading(true);
    const resp = await serviceGetWaCostReport(cookies.SIDikey, {
      startDate: summaryStart,
      endDate: summaryEnd,
      groupBy: "CHANNEL",
    });
    if (requestId !== latestSummaryRequest.current) return;
    setIsSummaryLoading(false);
    setSummary(resp.status === 200 ? resp.data : []);
  };

  const refreshRates = async () => {
    const resp = await serviceGetWaCostRates(cookies.SIDikey);
    if (resp.status === 200) setRates(resp.data);
  };

  useEffect(() => {
    if (isAllowed) refreshRates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAllowed) refreshSummary();
  }, [summaryMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report is only fetched while its tab is open.
  useEffect(() => {
    if (isAllowed && tab === "REPORT") refreshReport();
  }, [filter, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAllowed) return <Redirect to={`${process.env.PUBLIC_URL}/`} />;

  const updateFilter = (patch) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  // The report refetches by itself when its tab is opened again.
  const onRatesChanged = () => {
    refreshRates();
    refreshSummary();
  };

  const toggleSort = (key) => {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
    setPage(1);
  };

  const { rows } = report;
  const group = GROUP_OPTIONS.find((o) => o.value === report.groupBy);
  const isDateGroup = report.groupBy === "DAY" || report.groupBy === "MONTH";
  const sortField = sort.key === "label" && isDateGroup ? "key" : sort.key;
  const sortedRows = sortRows(rows, sortField, sort.dir);
  const pageRows = sortedRows.slice((page - 1) * TAKE, page * TAKE);
  const total = rows.reduce(
    (acc, r) => ({
      messageCount: acc.messageCount + r.messageCount,
      billableCount: acc.billableCount + r.billableCount,
      totalCost: acc.totalCost + r.totalCost,
    }),
    { messageCount: 0, billableCount: 0, totalCost: 0 },
  );
  const summaryCost = summary.reduce((acc, r) => acc + r.totalCost, 0);

  return (
    <React.Fragment>
      <Head title="WA Cost Monitoring"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle tag="h3" page>
              WA Cost Monitoring
            </BlockTitle>
            <p className="text-soft">Biaya pesan WhatsApp keluar. Kuota gratis per nomor direset setiap tanggal 1.</p>
          </BlockHeadContent>
        </BlockHead>

        <ul className="nk-nav nav nav-tabs">
          {TABS.map((t) => (
            <li key={t.key} className={`nav-item ${tab === t.key ? "active current-page" : ""}`}>
              <a
                className={`nav-link ${tab === t.key ? "active" : ""}`}
                href="#tabs"
                onClick={(ev) => {
                  ev.preventDefault();
                  setTab(t.key);
                }}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>

        {tab === "SUMMARY" && (
          <Block>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 8 }}>
              <input
                type="month"
                className="form-control w-auto position-relative"
                value={summaryMonth}
                max={currentMonth}
                required
                onChange={(e) => e.target.value && setSummaryMonth(e.target.value)}
              />
              <div className="text-soft">
                Total {moment(summaryMonth, MONTH).format("MMMM YYYY")}:{" "}
                <strong className="text-dark">{formatCurrencyIDR(summaryCost)}</strong>
              </div>
            </div>
            <Row className="g-gs" aria-busy={isSummaryLoading}>
              {isSummaryLoading &&
                // Same count as the cards being replaced, so the grid does not jump.
                Array.from({ length: summary.length || 3 }, (_, i) => <SummaryCardSkeleton key={i} />)}
              {!isSummaryLoading &&
                summary.map((item) => {
                  // Quota/rate in force at the end of the summarised month.
                  const rate = rateOn(rates, item.provider, summaryEnd);
                  const quota = rate?.freeQuota || 0;
                  const freeUsed = item.messageCount - item.billableCount;
                  const percent = quota ? Math.min(100, (freeUsed / quota) * 100) : 100;
                  const isOver = item.billableCount > 0;
                  return (
                    <Col md="6" xl="4" key={item.key}>
                      <div className="card card-bordered h-100">
                        <div className="card-inner">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="title mb-0">{item.label}</h6>
                            <span className="badge badge-dim badge-primary">
                              {PROVIDER_LABEL[item.provider] || item.provider}
                            </span>
                          </div>
                          <div className="amount h4 mb-1">{formatCurrencyIDR(item.totalCost)}</div>
                          <div className="text-soft mb-2">
                            {item.messageCount.toLocaleString("id-ID")} pesan ·{" "}
                            {item.billableCount.toLocaleString("id-ID")} berbayar
                            {rate ? ` × ${formatCurrencyIDR(rate.rate)}` : ""}
                          </div>
                          <Progress value={percent} color={isOver ? "danger" : "success"} className="progress-md" />
                          <div className="small text-soft mt-1">
                            Kuota gratis {Math.min(freeUsed, quota).toLocaleString("id-ID")} /{" "}
                            {quota.toLocaleString("id-ID")}
                            {isOver ? " — terlampaui" : ""}
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              {!isSummaryLoading && summary.length === 0 && (
                <Col size="12">
                  <div className="text-soft">Belum ada pesan keluar di bulan ini.</div>
                </Col>
              )}
            </Row>
          </Block>
        )}

        {tab === "REPORT" && (
          <Block>
            <DataTable className="card-stretch">
              <div className="card-inner position-relative card-tools-toggle">
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                  <input
                    type="date"
                    className="form-control w-auto position-relative"
                    value={filter.startDate}
                    max={filter.endDate}
                    required
                    onChange={(e) => e.target.value && updateFilter({ startDate: e.target.value })}
                  />
                  <span className="text-soft">s/d</span>
                  <input
                    type="date"
                    className="form-control w-auto position-relative"
                    value={filter.endDate}
                    min={filter.startDate}
                    required
                    onChange={(e) => e.target.value && updateFilter({ endDate: e.target.value })}
                  />
                  <select
                    className="form-control w-auto"
                    value={filter.groupBy}
                    onChange={(e) => updateFilter({ groupBy: e.target.value })}
                  >
                    {GROUP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-control w-auto"
                    value={filter.provider}
                    onChange={(e) => updateFilter({ provider: e.target.value })}
                  >
                    <option value="">Semua Provider</option>
                    {Object.entries(PROVIDER_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <Button color="danger" className="btn" onClick={() => updateFilter(defaultFilter())}>
                    <em className="icon ni ni-reload"></em>
                    <span>Reset</span>
                  </Button>
                </div>
              </div>
              {/* Previous rows stay visible, dimmed, while the new result loads. */}
              <DataTableBody style={isLoading ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
                <DataTableHead>
                  {COLUMNS.map((col) => (
                    <DataTableRow key={col.key} className={col.align}>
                      <span className="sub-text" style={{ cursor: "pointer" }} onClick={() => toggleSort(col.key)}>
                        {col.label || group?.column}
                        {sort.key === col.key && (
                          <em className={`icon ni ni-arrow-${sort.dir === "asc" ? "up" : "down"} ml-1`}></em>
                        )}
                      </span>
                    </DataTableRow>
                  ))}
                </DataTableHead>
                {pageRows.map((item) => (
                  <DataTableItem key={item.key}>
                    <DataTableRow>
                      <span className="tb-lead">{displayLabel(report.groupBy, item)}</span>
                      {item.provider && report.groupBy !== "PROVIDER" && (
                        <span className="d-block text-soft">{PROVIDER_LABEL[item.provider] || item.provider}</span>
                      )}
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <span className="tb-amount">{item.messageCount.toLocaleString("id-ID")}</span>
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <span className="tb-amount">{item.billableCount.toLocaleString("id-ID")}</span>
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <span className="tb-amount">{formatCurrencyIDR(item.totalCost)}</span>
                    </DataTableRow>
                  </DataTableItem>
                ))}
                {rows.length > 0 && (
                  <DataTableItem>
                    <DataTableRow>
                      <strong>Total ({rows.length.toLocaleString("id-ID")} baris)</strong>
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <strong>{total.messageCount.toLocaleString("id-ID")}</strong>
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <strong>{total.billableCount.toLocaleString("id-ID")}</strong>
                    </DataTableRow>
                    <DataTableRow className="text-right">
                      <strong>{formatCurrencyIDR(total.totalCost)}</strong>
                    </DataTableRow>
                  </DataTableItem>
                )}
              </DataTableBody>
              <div className="card-inner">
                {!isLoading && rows.length > TAKE && (
                  <PaginationComponent
                    itemPerPage={TAKE}
                    totalItems={rows.length}
                    paginate={setPage}
                    currentPage={page}
                  />
                )}
                {isLoading && (
                  <div className="text-center py-3">
                    <Spinner size="sm" color="primary" />
                    <span className="text-soft ml-2">Memuat data...</span>
                  </div>
                )}
                {!isLoading && error && <div className="text-center text-danger">{error}</div>}
                {!isLoading && !error && rows.length === 0 && (
                  <div className="text-center">
                    <span className="text-silent">No data found</span>
                  </div>
                )}
              </div>
            </DataTable>
          </Block>
        )}

        {tab === "RATES" && (
          <Block>
            <WaCostRates token={cookies.SIDikey} rates={rates} onChanged={onRatesChanged} />
          </Block>
        )}
      </Content>
    </React.Fragment>
  );
};

export default WaCostMonitoring;
